import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

interface DateTimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
}

function generateTimeRangeSlots() {
  const slots: { value: string; label: string }[] = [];
  for (let h = 7; h < 19; h++) {
    const start = `${String(h).padStart(2, '0')}:00`;
    const end = `${String(h + 1).padStart(2, '0')}:00`;
    slots.push({ value: start, label: `${start} - ${end}` });
  }
  return slots;
}

const timeRangeSlots = generateTimeRangeSlots();

export default function DateTimeModal({ isOpen, onClose, isDarkMode }: DateTimeModalProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'fa';

  const [selectedDate, setSelectedDate] = useState<string>(() => {
    return localStorage.getItem('selectedDeliveryDate') || '';
  });
  const [selectedTime, setSelectedTime] = useState<string>(() => {
    return localStorage.getItem('selectedDeliveryTime') || '';
  });
  const [pickupMode, setPickupMode] = useState<'courier' | 'self'>(() => {
    return localStorage.getItem('scheduledSubMode') === 'self' ? 'self' : 'courier';
  });

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Generate quick date options: Today, Tomorrow, Day After
  const quickDates = (() => {
    const today = new Date();
    const dates: { key: string; label: string; dayName: string; dayNum: string; monthName: string; isToday: boolean; isTomorrow: boolean; isDayAfter: boolean }[] = [];
    for (let i = 0; i < 3; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const dayNum = String(d.getDate());
      const monthName = d.toLocaleDateString(i18n.language === 'fa' ? 'fa-IR' : 'en-US', { month: 'short' });
      let dayName: string;
      let isToday = false;
      let isTomorrow = false;
      let isDayAfter = false;
      if (i === 0) {
        dayName = t('datetime.today') || 'Today';
        isToday = true;
      } else if (i === 1) {
        dayName = t('datetime.tomorrow') || 'Tomorrow';
        isTomorrow = true;
      } else {
        dayName = t('datetime.dayAfter') || 'Day After';
        isDayAfter = true;
      }
      dates.push({ key, label: `${dayName}, ${monthName} ${dayNum}`, dayName, dayNum, monthName, isToday, isTomorrow, isDayAfter });
    }
    return dates;
  })();

  const handleConfirm = () => {
    if (selectedDate && selectedTime) {
      localStorage.setItem('selectedDeliveryDate', selectedDate);
      localStorage.setItem('selectedDeliveryTime', selectedTime);
      localStorage.setItem('scheduledSubMode', pickupMode);
      window.dispatchEvent(new StorageEvent('storage'));
      onClose();
    }
  };

  const handleDateSelect = (dateKey: string) => {
    if (selectedDate === dateKey) {
      setSelectedDate('');
    } else {
      setSelectedDate(dateKey);
    }
  };

  const handleTimeSelect = (timeValue: string) => {
    if (selectedTime === timeValue) {
      setSelectedTime('');
    } else {
      setSelectedTime(timeValue);
    }
  };

  // Compute display info for selected date & time
  const selectedDateInfo = quickDates.find(d => d.key === selectedDate);
  const selectedTimeInfo = timeRangeSlots.find(s => s.value === selectedTime);

  // Determine time-of-day period
  const timeOfDay = useMemo(() => {
    const timeToCheck = selectedTime || (() => {
      const now = new Date();
      const h = now.getHours();
      if (h >= 7 && h < 19) return `${String(h).padStart(2, '0')}:00`;
      return '07:00';
    })();
    const hour = parseInt(timeToCheck.split(':')[0], 10);
    if (hour >= 7 && hour < 10) return { period: 'morning', icon: 'ri-sun-line', label: t('datetime.morning') || 'Morning', color: 'from-amber-400/30 to-amber-300/10', iconColor: 'text-amber-400' };
    if (hour >= 10 && hour < 14) return { period: 'noon', icon: 'ri-sun-fill', label: t('datetime.noon') || 'Noon', color: 'from-yellow-400/30 to-yellow-300/10', iconColor: 'text-yellow-400' };
    if (hour >= 14 && hour < 17) return { period: 'afternoon', icon: 'ri-sun-cloudy-line', label: t('datetime.afternoon') || 'Afternoon', color: 'from-orange-400/30 to-orange-300/10', iconColor: 'text-orange-400' };
    return { period: 'evening', icon: 'ri-moon-line', label: t('datetime.evening') || 'Evening', color: 'from-indigo-400/20 to-violet-300/10', iconColor: 'text-indigo-300' };
  }, [selectedTime, t]);

  // Determine bottom bar state
  const hasDate = !!selectedDate && !!selectedDateInfo;
  const hasTime = !!selectedTime && !!selectedTimeInfo;
  const isComplete = hasDate && hasTime;

  const bottomState: 'empty' | 'dateOnly' | 'timeOnly' | 'complete' = 
    isComplete ? 'complete' : hasDate ? 'dateOnly' : hasTime ? 'timeOnly' : 'empty';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[200] flex flex-col"
          onClick={onClose}
        >
          <motion.div
            initial={{ backdropFilter: 'blur(0px)' }}
            animate={{ backdropFilter: 'blur(16px) saturate(1.1)' }}
            exit={{ backdropFilter: 'blur(0px)' }}
            transition={{ duration: 0.35 }}
            className={`absolute inset-0 ${isDarkMode ? 'bg-black/55' : 'bg-white/55'}`}
          />

          <motion.div
            initial={{ y: '-100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '-100%', opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className={`relative z-10 w-full h-full flex flex-col overflow-hidden ${isDarkMode ? 'bg-dark-100/70' : 'bg-white/70'} backdrop-blur-2xl`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`flex items-center justify-between px-5 pt-4 pb-3 border-b ${isDarkMode ? 'border-dark-200/50' : 'border-gray-200/50'}`}>
              <div className="flex items-center gap-3">
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  transition={{ type: 'spring', stiffness: 150, damping: 15, mass: 1 }}
                  onClick={onClose}
                  className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-dark-200/60 hover:bg-dark-200 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} transition-colors cursor-pointer`}
                >
                  <i className={`${isRtl ? 'ri-arrow-right-line' : 'ri-arrow-left-line'} text-lg`} />
                </motion.button>
                <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('datetime.title') || 'Schedule Delivery'}</h3>
              </div>

              {/* Delivery / Pickup toggle with animated sliding pill */}
              <div
                className="relative flex items-center gap-1 p-1 rounded-[20px] border backdrop-blur-xl border-gold/20 bg-gold/5"
                style={{ direction: 'ltr' }}
              >
                {/* Courier */}
                <div className="relative">
                  {pickupMode === 'courier' && (
                    <motion.div
                      layoutId="modalModePill"
                      transition={{ type: 'spring', stiffness: 380, damping: 30, mass: 0.8 }}
                      className="absolute inset-0 rounded-[18px] bg-gradient-to-r from-gold-dark to-gold-light pointer-events-none"
                    />
                  )}
                  <button
                    onClick={() => setPickupMode('courier')}
                    className={`relative flex items-center gap-1 px-3 h-7 rounded-full transition-colors duration-300 cursor-pointer whitespace-nowrap z-10 ${
                      pickupMode === 'courier'
                        ? 'text-white'
                        : isDarkMode
                          ? 'text-stone-200 hover:bg-dark-200/20'
                          : 'text-stone-500 hover:bg-stone-100/50'
                    }`}
                  >
                    <i className={pickupMode === 'courier' ? 'ri-riding-fill' : 'ri-riding-line'} style={{ fontSize: '15px' }} />
                    <AnimatePresence mode="wait">
                      {pickupMode === 'courier' && (
                        <motion.span
                          key="modal-courier-label"
                          initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                          animate={{ opacity: 1, width: 'auto', marginLeft: 4 }}
                          exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                          transition={{ duration: 0.2, ease: 'easeOut' }}
                          className="text-[11px] font-medium overflow-hidden whitespace-nowrap"
                        >
                          {t('header.delivery') || 'Delivery'}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </button>
                </div>

                {/* Self Pickup */}
                <div className="relative">
                  {pickupMode === 'self' && (
                    <motion.div
                      layoutId="modalModePill"
                      transition={{ type: 'spring', stiffness: 380, damping: 30, mass: 0.8 }}
                      className="absolute inset-0 rounded-[18px] bg-gradient-to-r from-gold-dark to-gold-light pointer-events-none"
                    />
                  )}
                  <button
                    onClick={() => setPickupMode('self')}
                    className={`relative flex items-center gap-1 px-3 h-7 rounded-full transition-colors duration-300 cursor-pointer whitespace-nowrap z-10 ${
                      pickupMode === 'self'
                        ? 'text-white'
                        : isDarkMode
                          ? 'text-stone-200 hover:bg-dark-200/20'
                          : 'text-stone-500 hover:bg-stone-100/50'
                    }`}
                  >
                    <i className={pickupMode === 'self' ? 'ri-walk-fill' : 'ri-walk-line'} style={{ fontSize: '15px' }} />
                    <AnimatePresence mode="wait">
                      {pickupMode === 'self' && (
                        <motion.span
                          key="modal-pickup-label"
                          initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                          animate={{ opacity: 1, width: 'auto', marginLeft: 4 }}
                          exit={{ opacity: 0, width: 0, marginLeft: 0 }}
                          transition={{ duration: 0.2, ease: 'easeOut' }}
                          className="text-[11px] font-medium overflow-hidden whitespace-nowrap"
                        >
                          {t('header.pickup') || 'Pickup'}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 flex flex-col">
              {/* Time-of-day Indicator + Hint Messages */}
              <div className="max-w-lg mx-auto w-full px-5 pt-4 pb-1 flex-shrink-0">
                <div className="flex items-center gap-3">
                  {/* Left: Icon-only time-of-day badge (no text) */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    key={timeOfDay.period}
                    className={`inline-flex items-center justify-center w-11 h-11 rounded-full bg-gradient-to-r ${timeOfDay.color} border border-white/10 flex-shrink-0`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-sm`}>
                      <i className={`${timeOfDay.icon} ${timeOfDay.iconColor} text-base`}></i>
                    </div>
                  </motion.div>

                  






{/* Right: animated hint messages */}
<AnimatePresence mode="wait">
  {!hasDate && !hasTime && (
    <motion.div
      key="hint-both"
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium border ${
        isDarkMode
          ? 'bg-amber-500/10 border-amber-500/20 text-amber-400'
          : 'bg-amber-50 border-amber-200 text-amber-600'
      }`}
    >
      <i className="ri-information-line text-sm"></i>
      <span>
        {pickupMode === 'courier' 
          ? 'ساعت و تاریخ برای دلیوری انتخاب کنید' 
          : 'ساعت و تاریخ برای پیکاپ انتخاب کنید'}
      </span>
    </motion.div>
  )}

  {!hasDate && hasTime && (
    <motion.div
      key="hint-date"
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium border ${
        isDarkMode
          ? 'bg-amber-500/10 border-amber-500/20 text-amber-400'
          : 'bg-amber-50 border-amber-200 text-amber-600'
      }`}
    >
      <i className="ri-calendar-line text-sm"></i>
      <span>حالا تاریخ انتخاب کن</span>
    </motion.div>
  )}

  {hasDate && !hasTime && (
    <motion.div
      key="hint-time"
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium border ${
        isDarkMode
          ? 'bg-amber-500/10 border-amber-500/20 text-amber-400'
          : 'bg-amber-50 border-amber-200 text-amber-600'
      }`}
    >
      <i className="ri-time-line text-sm"></i>
      <span>حالا ساعت انتخاب کن</span>
    </motion.div>
  )}

  {hasDate && hasTime && (
    <motion.div
      key="hint-complete"
      initial={{ opacity: 0, scale: 0.8, x: 10 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, x: -10 }}
      transition={{ duration: 0.35, ease: 'easeInOut' }}
      className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium border ${
        isDarkMode
          ? 'bg-amber-500/10 border-amber-500/20 text-amber-400'
          : 'bg-amber-50 border-amber-200 text-amber-600'
      }`}
    >
      <span>
        {pickupMode === 'courier' 
          ? 'ساعت و تاریخ برای دلیوری انتخاب شد ' 
          : 'ساعت و تاریخ برای پیکاپ انتخاب شد '}
      </span>
      <i className="ri-check-double-line text-sm"></i>
    </motion.div>
  )}
</AnimatePresence>















                </div>
              </div>

              {/* Date Selection — Fixed */}
              <div className="max-w-lg mx-auto w-full px-5 pt-3 pb-3 flex-shrink-0">
                <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {t('datetime.selectDate') || 'Select Date'}
                </h4>
                <div className="flex gap-2.5">
                  {quickDates.map((date) => {
                    const isSelected = selectedDate === date.key;
                    return (
                      <motion.button
                        key={date.key}
                        whileTap={{ scale: 0.96 }}
                        onClick={() => handleDateSelect(date.key)}
                        className={`flex-1 flex flex-col items-center gap-1.5 py-3.5 px-2 rounded-2xl cursor-pointer transition-all duration-500 relative overflow-hidden ${
                          isSelected
                            ? 'bg-gradient-to-br from-gold-dark to-gold-light text-white shadow-lg shadow-gold/20'
                            : isDarkMode
                              ? 'bg-dark-200/25 text-gray-300 hover:bg-dark-200/40 border border-dark-200/20'
                              : 'bg-white/30 text-gray-500 hover:bg-stone-100/50 border border-stone-200/20'
                        }`}
                      >
                        {isSelected && (
                          <motion.div
                            layoutId="dateGlow"
                            className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gold-dark/30 to-gold-light/20"
                            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
                          />
                        )}
                        <span className={`relative z-10 text-[11px] font-semibold uppercase tracking-wide ${isSelected ? 'text-white/80' : isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                          {date.dayName}
                        </span>
                        <span className={`relative z-10 text-2xl font-extrabold leading-none ${isSelected ? 'text-white' : isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                          {date.dayNum}
                        </span>
                        <span className={`relative z-10 text-[10px] font-medium ${isSelected ? 'text-white/70' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {date.monthName}
                        </span>
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="relative z-10 w-1.5 h-1.5 rounded-full bg-white mt-0.5"
                          />
                        )}
                        {date.isToday && !isSelected && (
                          <span className={`absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-gold`} />
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Time Slots — Scrollable */}
              <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="max-w-lg mx-auto px-5 pb-5">
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {t('datetime.selectTime') || 'Select Time'}
                  </h4>
                  <div className="flex flex-col gap-1.5">
                    {timeRangeSlots.map((slot) => {
                      const isSelected = selectedTime === slot.value;
                      return (
                        <motion.button
                          key={slot.value}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: 0.03 * timeRangeSlots.indexOf(slot) }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleTimeSelect(slot.value)}
                          className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-all duration-300 cursor-pointer relative ${
                            isSelected
                              ? 'bg-gradient-to-r from-gold-dark to-gold-light text-white shadow-lg shadow-gold/20'
                              : isDarkMode
                                ? 'bg-dark-200/25 text-gray-300 hover:bg-dark-200/45 border border-dark-200/20'
                                : 'bg-white/30 text-gray-600 hover:bg-white/50 border border-stone-200/20'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isSelected
                              ? 'bg-white/20'
                              : isDarkMode
                                ? 'bg-dark-200/40'
                                : 'bg-stone-100/60'
                          }`}>
                            <i className={`ri-time-line ${isSelected ? 'text-white' : isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-sm`} />
                          </div>
                          <span className={`font-medium ${isSelected ? 'text-sm' : 'text-xs'}`}>{slot.label}</span>
                          {isSelected && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="absolute right-4 w-1.5 h-1.5 rounded-full bg-white"
                            />
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Dynamic Bottom Bar — single button, text changes by state */}
            <div className={`p-5 border-t ${isDarkMode ? 'border-dark-200/50' : 'border-gray-200/50'}`}>
              <AnimatePresence mode="wait">
                {/* === EMPTY: nothing selected === */}
                {bottomState === 'empty' && (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className={`w-full py-4 rounded-2xl font-medium text-sm text-center flex items-center justify-center gap-2.5 ${
                      isDarkMode
                        ? 'text-gold/60 bg-gold/5 border border-gold/15'
                        : 'text-gold-dark/60 bg-gold/3 border border-gold/15'
                    } cursor-default`}
                  >
                    <i className="ri-calendar-line text-base flex-shrink-0" />
                    <span className="whitespace-nowrap">{t('datetime.selectAndConfirm') || 'Select and confirm'}</span>
                  </motion.div>
                )}

                {/* === DATE ONLY: date selected, need time === */}
                {bottomState === 'dateOnly' && (
                  <motion.div
                    key="dateOnly"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className={`w-full py-4 px-4 rounded-2xl font-medium text-sm flex items-center justify-center gap-2 overflow-hidden ${
                      isDarkMode
                        ? 'bg-gold/5 border border-gold/20'
                        : 'bg-gold/3 border border-gold/20'
                    } cursor-default`}
                  >
                    <span className={`font-bold truncate min-w-0 ${isDarkMode ? 'text-gold' : 'text-gold-dark'}`}>
                      {selectedDateInfo?.dayName}, {selectedDateInfo?.monthName} {selectedDateInfo?.dayNum}
                    </span>
                    <span className={`opacity-40 flex-shrink-0 ${isDarkMode ? 'text-gold' : 'text-gold-dark'}`}>·</span>
                    <span className={`whitespace-nowrap flex-shrink-0 font-medium ${isDarkMode ? 'text-gold/70' : 'text-gold-dark/70'}`}>{t('datetime.selectTime') || 'Select time'}</span>
                  </motion.div>
                )}

                {/* === TIME ONLY: time selected, need date === */}
                {bottomState === 'timeOnly' && (
                  <motion.div
                    key="timeOnly"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className={`w-full py-4 px-4 rounded-2xl font-medium text-sm flex items-center justify-center gap-2 overflow-hidden ${
                      isDarkMode
                        ? 'bg-gold/5 border border-gold/20'
                        : 'bg-gold/3 border border-gold/20'
                    } cursor-default`}
                  >
                    <span className={`font-bold whitespace-nowrap flex-shrink-0 ${isDarkMode ? 'text-gold' : 'text-gold-dark'}`}>
                      {selectedTimeInfo?.label}
                    </span>
                    <span className={`opacity-40 flex-shrink-0 ${isDarkMode ? 'text-gold' : 'text-gold-dark'}`}>·</span>
                    <span className={`whitespace-nowrap flex-shrink-0 font-medium ${isDarkMode ? 'text-gold/70' : 'text-gold-dark/70'}`}>{t('datetime.selectDate') || 'Select date'}</span>
                  </motion.div>
                )}

                {/* === COMPLETE: both selected — CONFIRM === */}
                {bottomState === 'complete' && (
                  <motion.div
                    key="complete"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      onClick={handleConfirm}
                      className="w-full py-4 px-4 rounded-2xl font-semibold text-sm text-white text-center overflow-hidden bg-gradient-to-r from-gold-dark to-gold-light hover:shadow-xl hover:shadow-gold/30 border border-gold/20 cursor-pointer transition-all duration-500"
                    >
                      <span className="flex items-center justify-center gap-3 min-w-0">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 text-xs">
                          <span className="truncate max-w-[80px]">{selectedDateInfo?.dayName}, {selectedDateInfo?.dayNum} {selectedDateInfo?.monthName}</span>
                          <span className="opacity-60 flex-shrink-0">·</span>
                          <span className="whitespace-nowrap flex-shrink-0">{selectedTimeInfo?.label}</span>
                        </span>
                        <span className="font-bold">{t('common.confirm') || t('datetime.confirmSchedule') || 'Confirm'}</span>
                        <i className="ri-check-line text-lg flex-shrink-0"></i>
                      </span>
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}